import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import {
  ChevronRight,
  Edit,
  PlusCircle,
  Trash2,
  CheckCircle,
  XCircle,
  Zap,
  Mail,
  Phone,
  MapPin,
  Globe,
  User,
  Tag,
  Search,
  Users,
  FileText,
  Download,
  Plus,
  ChevronDown,
  ChevronUp,
  Calculator
} from 'lucide-react';
import './ProspectsManagement.css';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const DetailProspect = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const navigate = useNavigate();
  const {id} = useParams();

  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    age: '',
    email: '',
    telephone: '',
    adresse: '',
    category: 'Person',
    siteWeb: '',
    mapsUrl: '',
    etat: '0-contact',
    source: 'Web',
    dateContact: new Date().toISOString().split('T')[0],
    interesseProduit: '',
    assignedTo: '',
    note: []
  });

  const [newNote, setNewNote] = useState('');
  const [newEtat, setNewEtat] = useState('');
  const [newInteraction, setNewInteraction] = useState({
    typeInteraction: 'Appel',
    commentaire: ''
  });
  const [newDateContact, setNewDateContact] = useState('');

  // ─── État DEVIS PROSPECT ────────────────────────────────────────────────
  const [devisProspect, setDevisProspect] = useState([]);
  const [showDevisModal, setShowDevisModal] = useState(false);
  const [devisLoading, setDevisLoading] = useState(false);
  const [categoriesDB, setCategoriesDB] = useState([]);
  const [categoriesSaisie, setCategoriesSaisie] = useState([]);
  const [devisForm, setDevisForm] = useState({
    description: '',
    tauxTVA: 19,
    remise: 0,
    conditions: '',
    notes: '',
    dateCreation: new Date().toISOString().split('T')[0]
  });
  // ────────────────────────────────────────────────────────────────────────

  const getToken = () => localStorage.getItem('token');

  const goToList = () => {
    if (onNavigate) {
      onNavigate('prospects');
      return;
    }
    navigate('/prospects');
  };

  const loadProspect = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/prospects/${id}`, {
        headers: {Authorization: `Bearer ${getToken()}`}
      });
      const prospectData = response.data?.data || response.data;
      setProspect(prospectData);
      setNewEtat(prospectData?.etat || '');
      setNewDateContact(
        prospectData?.dateContact
          ? new Date(prospectData.dateContact).toISOString().split('T')[0]
          : ''
      );
    } catch (err) {
      setError('Erreur lors du chargement du prospect');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProspect();
    chargerCategories();
    chargerDevis();
  }, [id]);

  // ─── Helpers Devis ───────────────────────────────────────────────────────
  const nouvelleLigneDevis = () => ({ produitId: null, nomProduit: '', prixUnitaire: '', quantite: '', tva: 19, remise: 0 });

  const buildCatsSaisie = (cats) =>
    cats.map(c => ({
      catId: c._id,
      designation: c.label,
      expanded: false,
      lignes: [nouvelleLigneDevis()]
    }));

  const chargerCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/categories`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const cats = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setCategoriesDB(cats);
      setCategoriesSaisie(buildCatsSaisie(cats));
    } catch (e) {
      console.error('Erreur catégories:', e);
    }
  };

  const chargerDevis = async () => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/documents-financiers/prospect/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDevisProspect(res.data?.documents || []);
    } catch (e) {
      console.error('Erreur chargement devis:', e);
    }
  };

  const creerDevisProspect = async (e) => {
    e.preventDefault();
    // Aplatir les lignes en articles
    const articles = [];
    categoriesSaisie.forEach(cat => {
      (cat.lignes || []).forEach(ligne => {
        if (Number(ligne.quantite) > 0 && Number(ligne.prixUnitaire) > 0) {
          articles.push({
            designation: cat.designation,
            nomProduit: ligne.nomProduit || cat.designation,
            produitId: ligne.produitId || null,
            quantite: Number(ligne.quantite),
            prixUnitaire: Number(ligne.prixUnitaire),
            tva: Number(ligne.tva) || 19,
            remise: Number(ligne.remise) || 0
          });
        }
      });
    });
    if (articles.length === 0) {
      setError('Veuillez renseigner au moins une ligne de produit.');
      setTimeout(() => setError(''), 4000);
      return;
    }
    setDevisLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/documents-financiers/prospect/${id}`, {
        ...devisForm,
        articles: JSON.stringify(articles)
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setShowDevisModal(false);
      setCategoriesSaisie(buildCatsSaisie(categoriesDB));
      setDevisForm({ description: '', tauxTVA: 19, remise: 0, conditions: '', notes: '', dateCreation: new Date().toISOString().split('T')[0] });
      setSuccessMessage('Devis créé avec succès !');
      await chargerDevis();
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création du devis');
      setTimeout(() => setError(''), 4000);
    } finally {
      setDevisLoading(false);
    }
  };

  const telechargerDevis = async (docId, numero) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/documents-financiers/${docId}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Erreur lors du téléchargement');
      setTimeout(() => setError(''), 3000);
    }
  };
  // ────────────────────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const openEditModal = () => {
    if (!prospect) return;
    setFormData({
      nom: prospect.nom || '',
      prenom: prospect.prenom || '',
      age: prospect.age || '',
      email: prospect.email || '',
      telephone: prospect.telephone || '',
      adresse: prospect.adresse || '',
      category: prospect.category || 'Person',
      siteWeb: prospect.siteWeb || '',
      mapsUrl: prospect.mapsUrl || '',
      etat: prospect.etat || '0-contact',
      source: prospect.source || 'Web',
      dateContact: prospect.dateContact
        ? new Date(prospect.dateContact).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      interesseProduit: prospect.interesseProduit || '',
      assignedTo: prospect.assignedTo || '',
      note: prospect.note || []
    });
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prospect) return;
    setLoading(true);
    try {
      await axios.put(`${API_BASE_URL}/prospects/${prospect._id}`, formData, {
        headers: {Authorization: `Bearer ${getToken()}`}
      });
      setSuccessMessage('Prospect modifie avec succes');
      setShowEditModal(false);
      await loadProspect();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !prospect) return;
    try {
      const updatedNotes = [...(prospect.note || []), newNote];
      await axios.put(`${API_BASE_URL}/prospects/${prospect._id}`,
        {note: updatedNotes},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setProspect({...prospect, note: updatedNotes});
      setNewNote('');
      setSuccessMessage('Note ajoutee avec succes');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Erreur lors de l\'ajout de la note');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUpdateEtat = async () => {
    if (!newEtat || !prospect) return;
    try {
      await axios.put(`${API_BASE_URL}/prospects/${prospect._id}`,
        {etat: newEtat},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setProspect({...prospect, etat: newEtat});
      setSuccessMessage('Etat mis a jour avec succes');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Erreur lors de la mise a jour de l\'etat');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUpdateDateContact = async () => {
    if (!newDateContact || !prospect) return;
    try {
      await axios.put(`${API_BASE_URL}/prospects/${prospect._id}`,
        {dateContact: newDateContact},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setProspect({...prospect, dateContact: newDateContact});
      setSuccessMessage('Date de contact mise a jour');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Erreur lors de la mise a jour de la date');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleAddInteraction = async () => {
    if (!newInteraction.commentaire.trim() || !prospect) return;
    try {
      const updatedInteractions = [
        ...(prospect.historiqueInteractions || []),
        {...newInteraction, date: new Date()}
      ];
      await axios.put(`${API_BASE_URL}/prospects/${prospect._id}`,
        {historiqueInteractions: updatedInteractions},
        {headers: {Authorization: `Bearer ${getToken()}`}}
      );
      setProspect({...prospect, historiqueInteractions: updatedInteractions});
      setNewInteraction({typeInteraction: 'Appel', commentaire: ''});
      setSuccessMessage('Interaction ajoutee avec succes');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Erreur lors de l\'ajout de l\'interaction');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleConvertToClient = async () => {
    if (!prospect) return;
    if (!window.confirm('Convertir ce prospect en client? Un compte utilisateur sera cree automatiquement.')) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/prospects/${prospect._id}/convert`, {}, {
        headers: {Authorization: `Bearer ${getToken()}`}
      });
      setProspect({...prospect, convertedToClient: true, clientId: response.data?.clientId || prospect.clientId});
      setSuccessMessage(
        `Prospect converti! Login: ${response.data.credentials?.login}, Mot de passe: ${response.data.credentials?.password}`
      );
      setTimeout(() => setSuccessMessage(''), 10000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la conversion');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!prospect) return;
    if (!window.confirm('Supprimer ce prospect ?')) return;
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/prospects/${prospect._id}`, {
        headers: {Authorization: `Bearer ${getToken()}`}
      });
      setSuccessMessage('Prospect supprime avec succes');
      setTimeout(() => setSuccessMessage(''), 3000);
      goToList();
    } catch (err) {
      setError('Erreur lors de la suppression');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!id) {
    return (
      <div className="pm-backoffice-container">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
        <div className="pm-backoffice-content">
          <div className="alert alert-error">ID prospect manquant.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-backoffice-container">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <div className="pm-backoffice-content">
        {loading && (
          <div className="pm-loading-premium">
            <LoadingSpinner message="Chargement du prospect..." />
          </div>
        )}

        {!loading && prospect && (
          <div className="pm-prospect-detail-page">
            <div className="pm-premium-detail-header-card">
              <div className="pm-detail-header-info">
                <button className="pm-btn-premium-dark" onClick={goToList} style={{marginBottom: '15px', padding: '8px 16px', fontSize: '0.8rem'}}>
                  <ChevronRight size={16} style={{transform: 'rotate(180deg)'}} /> RETOUR A LA LISTE
                </button>
                <h2 style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  Fiche Complete : {prospect.nom} {prospect.prenom}
                </h2>
                <p>Consultation et gestion avancee du profil prospect</p>
              </div>
              <div className={`pm-premium-badge ${
                prospect.etat === '0-contact' ? 'pm-badge-new' :
                prospect.etat === 'Petit-contact' ? 'pm-badge-contacted' :
                prospect.etat === 'Relancé' ? 'pm-badge-proposition' :
                'pm-badge-hot'
              }`} style={{fontSize: '0.85rem', padding: '8px 16px'}}>
                {(prospect.etat || '').toUpperCase()}
              </div>
            </div>

            <div className="pm-premium-identity-card">
              <div className="pm-prospect-avatar-big">
                {prospect.prenom ? prospect.prenom[0] : ''}{prospect.nom ? prospect.nom[0] : ''}
              </div>
              <div className="pm-prospect-main-meta">
                <h2>{prospect.nom} {prospect.prenom}</h2>
                <p className="pm-prospect-sub-title">{prospect.category} • {prospect.source}</p>
                <div className="pm-prospect-tags">
                  <span className="pm-tag-badge pm-tag-blue">Nouveau</span>
                  <span className="pm-tag-badge pm-tag-gray">{prospect.category}</span>
                  <span className="pm-tag-badge pm-tag-green">{prospect.source}</span>
                </div>
              </div>
              <div className="pm-prospect-side-meta">
                <div className="pm-side-meta-item">
                  <strong>Insertion Systeme</strong>
                  {prospect.createdAt ? new Date(prospect.createdAt).toLocaleDateString('fr-FR', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  }) : '-'}
                </div>
                <div className="pm-side-meta-item">
                  <strong>ID Prospect</strong>
                  <span style={{fontFamily: 'monospace', fontSize: '0.75rem'}}>{prospect._id}</span>
                </div>
              </div>
            </div>

            <div className="pm-detail-actions-row">
              {hasPermission('prospects', 'update') && (
                <button className="pm-btn-detail-action pm-btn-outline-blue" onClick={openEditModal}>
                  <Edit size={18} /> Modifier
                </button>
              )}
              {!prospect.convertedToClient && (
                <button className="pm-btn-detail-action pm-btn-outline-green" onClick={handleConvertToClient}>
                  <PlusCircle size={18} /> Convertir en client
                </button>
              )}
              {hasPermission('documents', 'create') && (
                <button
                  className="pm-btn-detail-action"
                  style={{ background: 'linear-gradient(135deg, #1e3a5f, #2d5490)', color: 'white', border: 'none' }}
                  onClick={() => setShowDevisModal(true)}
                >
                  <FileText size={18} /> Nouveau Devis
                </button>
              )}
              {hasPermission('prospects', 'delete') && (
                <button className="pm-btn-detail-action pm-btn-outline-red" onClick={handleDelete}>
                  <Trash2 size={18} /> Supprimer
                </button>
              )}
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {/* ─── SECTION DEVIS ─────────────────────────────────────── */}
            <div className="pm-premium-detail-section" style={{ borderLeft: '4px solid #1e3a5f' }}>
              <div className="pm-section-header-premium">
                <div className="pm-section-icon-circle" style={{ background: '#eff6ff', color: '#1e3a5f' }}>
                  <FileText size={18} />
                </div>
                <h3 style={{ flex: 1 }}>Devis Prospect</h3>
                <span style={{
                  background: '#1e3a5f', color: 'white', padding: '2px 10px',
                  borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700
                }}>{devisProspect.length}</span>
              </div>

              {devisProspect.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  Aucun devis créé pour ce prospect.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {devisProspect.map(doc => (
                    <div key={doc._id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                      padding: '12px 16px'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>{doc.numero}</span>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {new Date(doc.dateCreation).toLocaleDateString('fr-FR')} &bull;
                          {' '}{doc.montantTTC?.toFixed(3) || doc.montant?.toFixed(3)} TND TTC
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
                          background: doc.statut === 'valide' ? '#dcfce7' : '#fef9c3',
                          color: doc.statut === 'valide' ? '#166534' : '#92400e'
                        }}>{doc.statut === 'valide' ? 'Validé' : 'Brouillon'}</span>
                        <button
                          onClick={() => telechargerDevis(doc._id, doc.numero)}
                          style={{
                            background: 'none', border: '1px solid #cbd5e1', borderRadius: '7px',
                            padding: '6px 10px', cursor: 'pointer', color: '#1e3a5f',
                            display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem'
                          }}
                          title="Télécharger PDF"
                        >
                          <Download size={14} /> PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* ──────────────────────────────────────────────────────── */}

            <div className="pm-premium-detail-section">
              <div className="pm-section-header-premium">
                <div className="pm-section-icon-circle" style={{background: '#eff6ff', color: '#3b82f6'}}>
                  <Mail size={18} />
                </div>
                <h3>Coordonnees</h3>
              </div>
              <div className="pm-info-grid-premium">
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label"><Mail size={14} /> Email</div>
                  <div className="pm-info-box-value">
                    {prospect.email ? <a href={`mailto:${prospect.email}`}>{prospect.email}</a> : 'Non renseigne'}
                  </div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label"><Phone size={14} /> Telephone</div>
                  <div className="pm-info-box-value">
                    {prospect.telephone ? <a href={`tel:${prospect.telephone}`}>{prospect.telephone}</a> : 'Non renseigne'}
                  </div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label"><MapPin size={14} /> Adresse</div>
                  <div className="pm-info-box-value">{prospect.adresse || 'Non renseignee'}</div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label"><User size={14} /> Age</div>
                  <div className="pm-info-box-value">{prospect.age ? `${prospect.age} ans` : 'Non renseigne'}</div>
                </div>
              </div>
            </div>

            <div className="pm-premium-detail-section">
              <div className="pm-section-header-premium">
                <div className="pm-section-icon-circle" style={{background: '#faf5ff', color: '#a855f7'}}>
                  <Globe size={18} />
                </div>
                <h3>Presence en Ligne</h3>
              </div>
              <div className="pm-info-grid-premium" style={{gridTemplateColumns: '1fr 1fr'}}>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label"><Globe size={14} /> Site Web</div>
                  <div className="pm-info-box-value">
                    {prospect.siteWeb ? <a href={prospect.siteWeb} target="_blank" rel="noopener noreferrer">{prospect.siteWeb}</a> : 'Aucun site web'}
                  </div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label"><MapPin size={14} /> Google Maps</div>
                  <div className="pm-info-box-value">
                    {prospect.mapsUrl ? <a href={prospect.mapsUrl} target="_blank" rel="noopener noreferrer">Voir sur Maps</a> : 'Pas de localisation'}
                  </div>
                </div>
              </div>
            </div>

            <div className="pm-premium-detail-section">
              <div className="pm-section-header-premium">
                <div className="pm-section-icon-circle" style={{background: '#fff7ed', color: '#f97316'}}>
                  <Tag size={18} />
                </div>
                <h3>Informations Commerciales</h3>
              </div>
              <div className="pm-info-grid-premium">
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Etat du Prospect</div>
                  <div className="pm-info-box-value">{prospect.etat}</div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Source</div>
                  <div className="pm-info-box-value">{prospect.source}</div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Date d'ajout</div>
                  <div className="pm-info-box-value">
                    {prospect.dateContact ? new Date(prospect.dateContact).toLocaleDateString('fr-FR', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    }) : 'Non specifiee'}
                  </div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Interet Produit</div>
                  <div className="pm-info-box-value">{prospect.interesseProduit || 'Non specifie'}</div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Commercial Assigne</div>
                  <div className="pm-info-box-value">{prospect.assignedTo || 'Non assigne'}</div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Categorie</div>
                  <div className="pm-info-box-value">{prospect.category}</div>
                </div>
              </div>
            </div>

            <div className="pm-premium-detail-section">
              <div className="pm-section-header-premium">
                <div className="pm-section-icon-circle" style={{background: '#f1f5f9', color: '#64748b'}}>
                  <Search size={18} />
                </div>
                <h3>Metadonnees</h3>
              </div>
              <div className="pm-info-grid-premium">
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Insertion Systeme</div>
                  <div className="pm-info-box-value">{prospect.createdAt ? new Date(prospect.createdAt).toLocaleString('fr-FR') : '-'}</div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">Derniere Modification</div>
                  <div className="pm-info-box-value">{prospect.updatedAt ? new Date(prospect.updatedAt).toLocaleString('fr-FR') : '-'}</div>
                </div>
                <div className="pm-info-box-premium">
                  <div className="pm-info-box-label">ID Prospect</div>
                  <div className="pm-info-box-value" style={{fontSize: '0.75rem', fontFamily: 'monospace'}}>{prospect._id}</div>
                </div>
                {prospect.convertedToClient && prospect.clientId && (
                  <div className="pm-info-box-premium">
                    <div className="pm-info-box-label">ID Client</div>
                    <div className="pm-info-box-value" style={{fontSize: '0.75rem', fontFamily: 'monospace'}}>{prospect.clientId}</div>
                  </div>
                )}
              </div>
            </div>

            {hasPermission('prospects', 'update') && (
              <>
                <div className="pm-premium-detail-section">
                  <div className="pm-section-header-premium">
                    <div className="pm-section-icon-circle" style={{background: '#eff6ff', color: '#3b82f6'}}>
                      <Zap size={18} />
                    </div>
                    <h3>Gestion Rapide</h3>
                  </div>
                  <div className="pm-quick-manage-grid">
                    <div className="pm-quick-form-group">
                      <label>Modifier l'Etat</label>
                      <div className="pm-quick-input-group">
                        <select
                          value={newEtat}
                          onChange={(e) => setNewEtat(e.target.value)}
                          className="pm-premium-select"
                          style={{flex: 1}}
                        >
                          <option value="0-contact">0-contact</option>
                          <option value="Petit-contact">Petit-contact</option>
                          <option value="Relancé">Relancé</option>
                          <option value="Prospect chaud">Prospect chaud</option>
                          <option value="Client potentiel">Client potentiel</option>
                        </select>
                        <button onClick={handleUpdateEtat} className="pm-btn-quick-update">
                          METTRE A JOUR
                        </button>
                      </div>
                    </div>
                    <div className="pm-quick-form-group">
                      <label>Modifier Date d'ajout</label>
                      <div className="pm-quick-input-group">
                        <input
                          type="date"
                          value={newDateContact}
                          onChange={(e) => setNewDateContact(e.target.value)}
                          className="pm-premium-input-search"
                          style={{flex: 1}}
                        />
                        <button onClick={handleUpdateDateContact} className="pm-btn-quick-update">
                          METTRE A JOUR
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pm-premium-detail-section">
                  <div className="pm-section-header-premium">
                    <div className="pm-section-icon-circle" style={{background: '#fffbeb', color: '#d97706'}}>
                      <Edit size={18} />
                    </div>
                    <h3>Notes</h3>
                  </div>
                  {prospect.note && prospect.note.length > 0 ? (
                    <div className="pm-premium-notes-container">
                      {prospect.note.map((note, index) => (
                        <div key={index} className="pm-note-audit-line">
                          • {note}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{color: '#94a3b8', fontStyle: 'italic', marginBottom: '16px'}}>Aucune note enregistree.</p>
                  )}
                  <div className="pm-note-input-area">
                    <input
                      type="text"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Ajouter une nouvelle note..."
                      className="pm-premium-input-search"
                      style={{flex: 1}}
                    />
                    <button onClick={handleAddNote} className="pm-btn-add-note">
                      + AJOUTER
                    </button>
                  </div>
                </div>

                <div className="pm-premium-detail-section">
                  <div className="pm-section-header-premium">
                    <div className="pm-section-icon-circle" style={{background: '#f8fafc', color: '#1e293b'}}>
                      <Users size={18} />
                    </div>
                    <h3>Historique des Interactions</h3>
                  </div>
                  <div className="pm-premium-notes-container" style={{background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b'}}>
                    {prospect.historiqueInteractions && prospect.historiqueInteractions.length > 0 ? (
                      prospect.historiqueInteractions.map((interaction, index) => (
                        <div key={index} style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                            <strong style={{color: '#1e293b'}}>{interaction.typeInteraction}</strong>
                            <span style={{fontSize: '0.75rem'}}>{new Date(interaction.date).toLocaleDateString('fr-FR')}</span>
                          </div>
                          <div style={{fontSize: '0.85rem'}}>{interaction.commentaire}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{textAlign: 'center', padding: '20px'}}>Aucune interaction enregistree.</div>
                    )}
                  </div>

                  <div className="pm-interaction-form-premium">
                    <h4 style={{margin: '0 0 16px 0', fontSize: '0.9rem'}}>Nouvelle Interaction</h4>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      <select
                        value={newInteraction.typeInteraction}
                        onChange={(e) => setNewInteraction({...newInteraction, typeInteraction: e.target.value})}
                        className="pm-premium-select"
                      >
                        <option value="Appel">Appel</option>
                        <option value="Email">Email</option>
                        <option value="Reunion">Reunion</option>
                        <option value="Visite">Visite</option>
                        <option value="Autre">Autre</option>
                      </select>
                      <textarea
                        value={newInteraction.commentaire}
                        onChange={(e) => setNewInteraction({...newInteraction, commentaire: e.target.value})}
                        placeholder="Commentaire de l'interaction..."
                        rows="3"
                        className="pm-premium-input-search"
                        style={{padding: '12px'}}
                      />
                      <button onClick={handleAddInteraction} className="pm-btn-submit-interaction">
                        + AJOUTER L'INTERACTION
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {showEditModal && (
              <div className="modal-overlay">
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2><Edit size={24} /> Modifier le Prospect</h2>
                    <button className="btn-close" onClick={() => setShowEditModal(false)}><XCircle size={24} /></button>
                  </div>
                  <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nom</label>
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleInputChange}
                          placeholder="Nom de famille"
                        />
                      </div>
                      <div className="form-group">
                        <label>Prenom</label>
                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleInputChange}
                          placeholder="Prenom"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="exemple@email.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>Telephone</label>
                        <input
                          type="text"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleInputChange}
                          placeholder="06 00 00 00 00"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Age</label>
                        <input
                          type="number"
                          name="age"
                          value={formData.age}
                          onChange={handleInputChange}
                          min="0"
                          max="150"
                        />
                      </div>
                      <div className="form-group">
                        <label>Categorie</label>
                        <select name="category" value={formData.category} onChange={handleInputChange}>
                          <option value="Person">Person</option>
                          <option value="Industrial">Industrial</option>
                          <option value="Industrial Company">Industrial Company</option>
                          <option value="School">School</option>
                          <option value="Government">Government</option>
                          <option value="Hospital">Hospital</option>
                          <option value="Hotel">Hotel</option>
                          <option value="Restaurant">Restaurant</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Adresse</label>
                      <input
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleInputChange}
                        placeholder="Adresse complete"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Site Web</label>
                        <input
                          type="text"
                          name="siteWeb"
                          value={formData.siteWeb}
                          onChange={handleInputChange}
                          placeholder="https://example.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>Google Maps URL</label>
                        <input
                          type="text"
                          name="mapsUrl"
                          value={formData.mapsUrl}
                          onChange={handleInputChange}
                          placeholder="https://maps.google.com/..."
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Etat</label>
                        <select name="etat" value={formData.etat} onChange={handleInputChange}>
                          <option value="0-contact">0-contact</option>
                          <option value="Petit-contact">Petit-contact</option>
                          <option value="Relancé">Relancé</option>
                          <option value="Prospect chaud">Prospect chaud</option>
                          <option value="Client potentiel">Client potentiel</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Source</label>
                        <select name="source" value={formData.source} onChange={handleInputChange}>
                          <option value="Web">Web</option>
                          <option value="Referral">Referral</option>
                          <option value="Salon">Salon</option>
                          <option value="Appel sortant">Appel sortant</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Date de Contact</label>
                        <input
                          type="date"
                          name="dateContact"
                          value={formData.dateContact}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Interesse par</label>
                        <input
                          type="text"
                          name="interesseProduit"
                          value={formData.interesseProduit}
                          onChange={handleInputChange}
                          placeholder="Produit ou service"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Assigne a</label>
                      <input
                        type="text"
                        name="assignedTo"
                        value={formData.assignedTo}
                        onChange={handleInputChange}
                        placeholder="Commercial responsable"
                      />
                    </div>

                    <div className="modal-footer">
                      <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                        Annuler
                      </button>
                      <button type="submit" className="btn-submit" disabled={loading}>
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailProspect;
